/**
 * sourceKey.js
 *
 * The semantic source contract for a dashboard widget (AVA-471, points #7/#9; see
 * .claude/plans/DASHBOARD_MULTI_SELECTION.md decisions 5 and 6). A `sourceKey` is a stable,
 * per-scope fingerprint - `v1|{facilityId}|{tableName}|{tagStrategy}|{filterScopeHash}` -
 * that the cross-widget selection/coloring rules (#10) check before combining or coloring
 * across widgets: two charts on the SAME facility + table + query scope share one key, while
 * differently-filtered charts on the same table get different keys. `tagStrategy` distinguishes
 * a per-facility materialized register from a plain view; `filterScopeHash` folds the widget's
 * saved filters into the scope so a chart filtered to System=X is not treated as compatible
 * with the same table unfiltered. The wizard persists the built key inside `sqlViewConfig`
 * (client-only field, survives the typed-DTO drop); `resolveSourceKey` derives the same value
 * at read time for legacy widgets that predate persistence, with the persisted value winning.
 *
 * PURE module - no Vuex, no components, no other project imports. Every function is TOTAL:
 * it never throws and always returns a defined result (a key string, a strategy, or null).
 */

// Per-facility register data sources are the materialized `vw_Register_<name>_<hash>` tables.
// Mirrors the isRegister primitive used at EditSqlViewWidget.vue:1875 and SqlViewDataGrid.vue:394.
const REGISTER_TABLE_PREFIX = 'vw_Register_';

// The two tag-resolution strategies a data source can use.
export const TAG_STRATEGY = Object.freeze({
  REGISTER: 'register',
  VIEW: 'view',
});

/**
 * The tag-resolution strategy for a data source: REGISTER for the per-facility materialized
 * `vw_Register_*` tables, VIEW for everything else (including junk/absent input). Total.
 * @param {*} tableName
 * @returns {string} one of TAG_STRATEGY
 */
export function tagStrategyForTable(tableName) {
  return typeof tableName === 'string' && tableName.startsWith(REGISTER_TABLE_PREFIX)
    ? TAG_STRATEGY.REGISTER
    : TAG_STRATEGY.VIEW;
}

/**
 * djb2 (xor variant) string hash rendered as an unsigned 32-bit hex digest. Local and
 * dependency-free so the same input yields the same digest on any machine/run. NOT
 * cryptographic - just a compact, stable fingerprint of a normalized filter scope.
 * @param {string} str
 * @returns {string}
 */
function djb2Hex(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // (hash * 33) ^ charCode, held in 32-bit space.
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}

/**
 * Canonical string form of a single filter value, stable across formatting differences and
 * type-preserving so distinct values never collide (5 vs '5', 'Open' vs '' vs null). The value
 * is DATA, not an identifier, so it is never lowercased - tag values are case-sensitive.
 * @param {*} value
 * @returns {string}
 */
function stableFilterValue(value) {
  return JSON.stringify(value ?? null);
}

/**
 * Stable hash of a widget's saved filter scope. Order-independent (filters are sorted) and
 * key-order-independent (each filter is reduced to an explicit [column, operator, value] tuple,
 * not its raw object). Column and operator are lowercased - both are SQL identifiers/canonical
 * tokens and case-insensitive - while the value keeps its case. Absent or empty filters return
 * the literal string `'none'`, so an unfiltered source has a well-known, non-hashed scope.
 * @param {Array<{ column?: *, operator?: *, value?: * }>} filters
 * @returns {string}
 */
export function filterScopeHash(filters) {
  if (!Array.isArray(filters) || filters.length === 0) return 'none';

  const normalized = filters
    .map((f) => {
      const column = String(f && f.column != null ? f.column : '').toLowerCase();
      const operator = String(f && f.operator != null ? f.operator : '').toLowerCase();
      const value = stableFilterValue(f ? f.value : undefined);
      return [column, operator, value];
    })
    // Null-byte join gives a total order with no delimiter collision against filter contents.
    .sort((a, b) => {
      const ka = a.join(' ');
      const kb = b.join(' ');
      if (ka < kb) return -1;
      if (ka > kb) return 1;
      return 0;
    });

  return djb2Hex(JSON.stringify(normalized));
}

/**
 * Build a widget's `sourceKey` from its scope parts. Returns null when any load-bearing part is
 * missing (no facility, no table) rather than a partial key - a partial key would falsely match
 * or mismatch in #10's compatibility checks. `tagStrategy` and `filterScopeHash` are always
 * derivable, so the only null cause is a missing facilityId or tableName. Total.
 * @param {{ facilityId?: *, tableName?: *, filters?: Array }} [parts]
 * @returns {string|null}
 */
export function buildSourceKey({ facilityId, tableName, filters } = {}) {
  if (!facilityId || !tableName) return null;
  const strategy = tagStrategyForTable(tableName);
  return `v1|${facilityId}|${tableName}|${strategy}|${filterScopeHash(filters)}`;
}

/**
 * Resolve a widget's `sourceKey` at read time. Accepts a `sqlViewConfig` object or its JSON
 * string form. A persisted `config.sourceKey` wins when present (the wizard wrote the exact
 * scope it saved under); otherwise the key is derived from `config.tableName` + `config.filters`
 * so legacy widgets that predate persistence still resolve to the same value. Returns null when
 * the config is unusable or underivable (no facility, no table). Total: never throws.
 * @param {object|string} sqlViewConfig
 * @param {*} facilityId
 * @returns {string|null}
 */
export function resolveSourceKey(sqlViewConfig, facilityId) {
  let config = sqlViewConfig;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch (err) {
      return null;
    }
  }
  if (!config || typeof config !== 'object') return null;

  if (typeof config.sourceKey === 'string' && config.sourceKey) return config.sourceKey;

  return buildSourceKey({
    facilityId,
    tableName: config.tableName,
    filters: config.filters,
  });
}
